/* Mixin README */
/* 

  This mixin can be used for any components that implement the dropdown.vue component and
  exposes the sleek-filter-input.vue component.

  Visual structure should be...

  <dropdown>
    ...
    <template #content>
      <div>
        <sleek-filter-input... />
        <ul>
          <li... />
        </ul>
        ...
      </div>
    </template>
  </dropdown>

  You should implement the following...

  * Add ref="dropdownRef" on <dropdown> component
  * Add @open="handleDropdownOpen" on <dropdown> component
  * Add ref="sleekFilterInputRef" on <sleek-filter-input> component. This ensures input is focused on exposure.
  * Implement the totalOptionsCount() computed property to handle when search input is displayed
  * Though not prescribed in this mixin, you should have a filteredOptions() computed property that evaluates
    the options based on this mixin's filterQuery value

  Emits both a .sync version to selectedId(s) and 'selected-id(s)-updated' so any implementers
  of the components that implement this mixin can choose the kind of event to respond to
*/

/* General */
import Vue from 'vue'
import { debounce, isEqual } from 'lodash'

export default Vue.extend({
  props: {
    /* Use for single-selects */
    selectedId: {
      type: Number,
      default: null,
    },
    /* Use for multi-selects */
    selectedIds: {
      type: Array as () => number[],
      default: null,
    },
  },

  data() {
    return {
      filterQuery: '',
      internalSelectedID: null,
      internalSelectedIDs: [] as number[],
      debounceEmitSelectedIDs: null,

      // Configurable...
      minItemsForFilterInput: 5,
      allowMultipleSelections: false,
      emitDebounceTimeForMultipleSelectionsInMS: 500,
    }
  },

  computed: {
    showFilterInput(): boolean {
      return (
        // Check specifically for 0 first for ifp-filter-customers
        // case where the load occurs after the dropdown is open
        // so there's no delay while options load
        this.minItemsForFilterInput === 0 ||
        this.totalOptionsCount > this.minItemsForFilterInput
      )
    },
    totalOptionsCount(): number {
      // Override in implementing component...
      return null
    },
  },

  watch: {
    selectedId: {
      immediate: true,
      handler(val) {
        this.internalSelectedID = val
      },
    },
    selectedIds: {
      immediate: true,
      handler(val) {
        this.internalSelectedIDs = val
      },
    },
    /* Needed for components that don't pass in options immediately and so
       need the focus to happen once totalOptionsCount has been hydrated 
       (e.g. ifp-filter-creators.vue) */
    showFilterInput: {
      immediate: true,
      handler(val) {
        if (val) {
          this.focusFilterInput()
        }
      },
    },
  },

  created() {
    this.debounceEmitSelectedIDs = debounce(
      this._emitSelectedIDs,
      this.emitDebounceTimeForMultipleSelectionsInMS,
    )
  },

  methods: {
    isSelected(id: number) {
      if (this.allowMultipleSelections) {
        return this.internalSelectedIDs.includes(id)
      }
      return this.internalSelectedID === id
    },
    handleSelection(id: number, emitImmediately?: boolean) {
      if (this.allowMultipleSelections) {
        if (this.internalSelectedIDs.includes(id)) {
          this._updateSelectedIDs(
            this.internalSelectedIDs.filter(i => i !== id),
            emitImmediately,
          )
        } else {
          this._updateSelectedIDs(
            [...this.internalSelectedIDs, id],
            emitImmediately,
          )
        }
      } else {
        this._updateSelectedID(id)
      }
    },
    clearSelectedIDs() {
      /* Circumvent debounce and emit immediately */
      this._updateSelectedIDs([], true)
    },
    clearFilterQuery() {
      this.filterQuery = ''
    },
    handleDropdownOpen() {
      if (this.showFilterInput) {
        this.focusFilterInput()
      }
    },
    focusFilterInput() {
      this.$nextTick(() => {
        // @ts-ignore
        this.$refs.sleekFilterInputRef.focusInput()
      })
    },
    _updateSelectedID(id: number) {
      // Set this so the UI can instantly react to the updated IDs rather than waiting
      // for syncing on the selectedId prop
      this.internalSelectedID = id
      this.$emit('selected-id-updated', this.internalSelectedID)
      this.$emit('update:selectedId', this.internalSelectedID)
      // @ts-ignore
      this.$refs.dropdownRef.handleClose()
    },
    _updateSelectedIDs(ids: number[], emitImmediately?: boolean) {
      // Don't emit if equal
      // Reason for adding: For ifp-filter-statuses, we have a special case where this method
      // is called on created() to rehydrate ids based on matching status names. This would
      // always trigger an update (even if equal) which would trigger itemTraitSet watch
      // that caused a double call to fetch results on all-landing.mixin and entity-landing.mixin
      if (isEqual(ids, this.internalSelectedIDs)) {
        return
      }
      // Set this so the UI can instantly react to the updated IDs rather than waiting
      // for syncing on the selectedIds prop
      this.internalSelectedIDs = ids
      if (emitImmediately) {
        this._emitSelectedIDs()
      } else {
        this.debounceEmitSelectedIDs()
      }
    },
    _emitSelectedIDs() {
      this.$emit('selected-ids-updated', this.internalSelectedIDs)
      this.$emit('update:selectedIds', this.internalSelectedIDs)
    },
  },
})
